import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
} from "@mui/material";
import { Dispatch, SetStateAction, memo, useState, useEffect } from "react";
import CustomDatePicker from "../../../../../components/CustomDatePicker";
import { INVALID_DATE_MESSAGE } from "@/components/filter/filter-constant";
import { isInvalidDate } from "@/utils/date-time-utility";
import Flex from "../../../../../components/Flex";
import GlobalFilter from "../../../../../components/GlobalFilter";
import Input from "../../../../../components/Input";
import SearchInput from "../../../../../components/SearchInput";
import Text from "../../../../../components/Text";
import CustomTabs from "../../../../../components/app/CustomTabs";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../hooks/use-debounce";
import useDisclosure from "../../../../../hooks/use-disclosure";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  getFormattedId,
  getXtenantId,
  roleFilter,
} from "../../../../../utils/utils";
import {
  DEFAULT_FILTERS,
  PATIENT_PAYMENT_FILTER_INITIAL_EXPANDED,
  PATIENT_PAYMENT_TABS,
  PatientPaymentFilters,
  PATIENT_PAYMENT_FILTER_KEYS,
  PATIENT_PAYMENT_MODE_OPTIONS,
  INSURANCE_PAYMENT_MODE_OPTIONS,
} from "./patient-payments-constant";
import UpsertPatientPayments from "./UpsertPatientPayments";
import AsyncSelect from "../../../../../components/AsyncSelect";
import AsyncSelectScroll from "../../../../../components/AsyncSelectScroll";
import { usePatientsList } from "../../../../../queries/use-get-patients";
import { startCase } from "lodash";
import { useSuperBillListV2 } from "../../../../../queries/billings/use-superBill-list-v2";
import { formatDateTime } from "../../../../../utils/date-time-utility";
import { useAppointmentsList } from "../../../../../queries/use-get-appointments";
import useActiveComponentCountController from "../../../../../components/filter/use-ActiveComponentCountController";
import { useRoleController } from "../../../../../components/useRoleController";

interface CollaborationTopBarProps {
  activePatientPayment: string;
  setActivePatientPayment: (v: string) => void;
  setSearchParams: (v: { activePatientPayment: string }) => void;
  setFilters: Dispatch<SetStateAction<PatientPaymentFilters>>;
  filters: PatientPaymentFilters;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  expanded: Record<string, boolean>;
  children?: React.ReactNode;
}

const ReferralTopBar = ({
  activePatientPayment,
  setActivePatientPayment,
  setSearchParams,
  setFilters,
  filters,
  setSearchStringKey,
  searchStringKey,
  setExpanded,
  expanded,
  children,
}: CollaborationTopBarProps) => {
  const { hasNone: hasNonePayments } = useRoleController("Patient payments");
  const { hasNone: hasNoneInsurancePayments } =
    useRoleController("Insurance Payments");

  return (
    <>
      <Flex
        justifyContent={"space-between"}
        alignItems={"center"}
        height={"2.5rem"}
      >
        <CustomTabs
          tabsData={roleFilter({
            list: PATIENT_PAYMENT_TABS,
            compWith: {
              patientPayment: !hasNonePayments,
              insurancePayment: !hasNoneInsurancePayments,
            },
          })}
          value={activePatientPayment}
          onChange={(v) => {
            setActivePatientPayment(v);
            setSearchParams({ activePatientPayment: v });
            // this is to clear the search string and filters on tab change
            setSearchStringKey?.("");
            setFilters(DEFAULT_FILTERS);
          }}
        />
        <EndActions
          setFilters={setFilters}
          filters={filters}
          activePatientPayment={activePatientPayment}
          setSearchStringKey={setSearchStringKey}
          searchStringKey={searchStringKey}
          expanded={expanded}
          setExpanded={setExpanded}
        />
      </Flex>
      {children}
    </>
  );
};

export default ReferralTopBar;

interface EndActionsProps {
  setFilters: Dispatch<SetStateAction<PatientPaymentFilters>>;
  filters: PatientPaymentFilters;
  activePatientPayment: string;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  setExpanded: Dispatch<SetStateAction<Record<string, boolean>>>;
  expanded: Record<string, boolean>;
}

const EndActions = memo(
  ({
    setFilters,
    filters,
    activePatientPayment,
    setExpanded,
    expanded,
    setSearchStringKey,
    searchStringKey,
  }: EndActionsProps) => {
    const [localFilters, setLocalFilters] =
      useState<PatientPaymentFilters>(filters);
    useEffect(() => {
      setLocalFilters(filters);
    }, [filters]);
    const patientPaymentDisclosureHost = useDisclosure();
    // console.log(setSearchStringKey, searchStringKey);

    // const [searchProviderKey, setSearchProviderKey] = useDebounce<string>("");
    // const { data: providersList } = useProvidersList({
    //   pagination: DEF_PAGINATION,
    //   searchKey: searchProviderKey,
    //   xTENANTID: getXtenantId(),
    // });
    const { hasUpdate: hasUpdatePayments } =
      useRoleController("Patient payments");
    const { hasUpdate: hasUpdateInsurancePayments } =
      useRoleController("Insurance Payments");
    const [searchPatientGroupKey, setSearchPatientGroupKey] =
      useDebounce<string>("");
    const [searchSuperBillGroupKey, setSearchSuperBillGroupKey] =
      useDebounce<string>("");
    const [searchAppointmentsGroupKey, setSearchAppointmentsGroupKey] =
      useDebounce<string>("");
    const [patientPagination, setPatientPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const [superBillPagination, setSuperBillPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const [appointmentsPagination, setAppointmentsPagination] =
      useState<DGT_Pagination>(DEF_PAGINATION);
    const {
      data: patientList,
      isLoading: patientLoading,
      isRefetching,
    } = usePatientsList({
      searchKey: searchPatientGroupKey,
      pagination: patientPagination,
      providerGroupId: getXtenantId(),
    });

    const {
      data: superBillList,
      isLoading: isLoadingSuperBill,
      isFetching: isFetchingSuperBill,
    } = useSuperBillListV2({
      pagination: superBillPagination,
      searchKey: searchSuperBillGroupKey,
      xTENANTID: getXtenantId(),
    });

    const {
      data: appointmentsList,
      isLoading: isLoadingAppointments,
      isFetching: isFetchingAppointments,
    } = useAppointmentsList({
      pagination: appointmentsPagination,
      providerGroupId: getXtenantId(),
      appointmentStatus: "COMPLETED",
      archieve: false,
      searchKey: searchAppointmentsGroupKey,
    });

    const hasInvalidDate = isInvalidDate(localFilters.transactionDate);

    const handleApply = () => {
      if (hasInvalidDate) return;
      setFilters(localFilters);
      setAnchorEl(null);
    };

    const handleCancel = () => {
      setLocalFilters(DEFAULT_FILTERS);
      setFilters(DEFAULT_FILTERS);
      setAnchorEl(null);
    };

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleExpandClick = (key: string, newExpanded: boolean) => {
      setExpanded({
        ...PATIENT_PAYMENT_FILTER_INITIAL_EXPANDED,
        [key]: newExpanded,
      });
    };

    const activeFilterCount = useActiveComponentCountController(filters);

    return (
      <Flex gap={1} flex={1} alignItems={"center"} justifyContent={"end"}>
        <SearchInput
          searchKey={searchStringKey ?? ""}
          onSearch={(val) => setSearchStringKey?.(val)}
          // The placeholder used to offer Tx ID / Cheque ID, which the search
          // does not match - it names only the fields that are searchable.
          placeholder={
            activePatientPayment === "patient-payment"
              ? "Search Patient Name"
              : "Search Provider Name / Patient Name"
          }
          sx={{ width: "22rem" }}
        />

        <GlobalFilter
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          activeFilterCount={activeFilterCount}
        >
          <Flex width={"100%"} justifyContent={"end"}>
            <Button variant="text" onClick={() => handleCancel()}>
              Clear Filter
            </Button>
          </Flex>

          {/* todo: need to separate the filter component for filters */}
          {PATIENT_PAYMENT_FILTER_KEYS.filter((it) =>
            activePatientPayment === "insurance-payment"
              ? it !== "invoiceId"
              : it !== "claimId"
          ).map((it) => {
            return it === "transactionDate" ? (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <CustomDatePicker
                      value={
                        localFilters[it as keyof PatientPaymentFilters] ?? null
                      }
                      onChange={(val) =>
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof PatientPaymentFilters]: val,
                        })
                      }
                      error={hasInvalidDate ? INVALID_DATE_MESSAGE : undefined}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            ) : it === "patientName" ? (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <AsyncSelectScroll
                      loading={patientLoading || isRefetching}
                      pagination={patientPagination}
                      setPagination={setPatientPagination}
                      totalElements={patientList?.data?.page?.totalElements}
                      value={localFilters.patientName}
                      placeholder="Search Patient"
                      onSearch={(val) => {
                        setSearchPatientGroupKey(val);
                        setPatientPagination((p) => ({ ...p, page: 0 }));
                      }}
                      options={patientList?.data?.content?.map((it) => ({
                        value: it?.uuid,
                        label: it?.firstName + " " + it?.lastName,
                      }))}
                      onChange={(val) => {
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof PatientPaymentFilters]: val,
                        });
                      }}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            ) : it === "visitId" ? (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <AsyncSelectScroll
                      loading={isLoadingAppointments || isFetchingAppointments}
                      pagination={appointmentsPagination}
                      setPagination={setAppointmentsPagination}
                      totalElements={
                        appointmentsList?.data?.page?.totalElements
                      }
                      value={localFilters.visitId}
                      placeholder="Search Visit"
                      onSearch={(val) => {
                        setSearchAppointmentsGroupKey(val);
                        setAppointmentsPagination((p) => ({ ...p, page: 0 }));
                      }}
                      options={appointmentsList?.data?.content?.map((it) => ({
                        value: it?.uuid,
                        label: `${getFormattedId(it?.uuid)} - ${formatDateTime(
                          it?.startTime
                        )}`,
                      }))}
                      onChange={(val) => {
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof PatientPaymentFilters]: val,
                        });
                      }}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            ) : it === "invoiceId" ? (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <AsyncSelectScroll
                      loading={isLoadingSuperBill || isFetchingSuperBill}
                      pagination={superBillPagination}
                      setPagination={setSuperBillPagination}
                      totalElements={superBillList?.data?.page?.totalElements}
                      value={localFilters.superbillId}
                      placeholder="Search Invoice Id"
                      onSearch={(val) => {
                        setSearchSuperBillGroupKey(val);
                        setSuperBillPagination((p) => ({ ...p, page: 0 }));
                      }}
                      options={superBillList?.data?.content?.map((it) => ({
                        value: it?.uuid,
                        label: `${getFormattedId(it?.uuid)} - ${formatDateTime(
                          it?.billDate
                        )}`,
                      }))}
                      onChange={(val) => {
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof PatientPaymentFilters]: val,
                        });
                      }}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            ) : it === "paymentMethod" ? (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <AsyncSelect
                      loading={patientLoading || isRefetching}
                      value={localFilters.paymentMethod}
                      placeholder="Search Payment Method"
                      onSearch={(val) => setSearchPatientGroupKey(val)}
                      options={(activePatientPayment === "insurance-payment"
                        ? INSURANCE_PAYMENT_MODE_OPTIONS
                        : PATIENT_PAYMENT_MODE_OPTIONS
                      )?.map((it) => ({
                        value: it?.value,
                        label: it?.label,
                      }))}
                      onChange={(val) => {
                        setLocalFilters({
                          ...localFilters,
                          [it as keyof PatientPaymentFilters]: val,
                        });
                      }}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            ) : (
              <Accordion
                key={it}
                variant="outlined"
                expanded={expanded[it]}
                onChange={(_, newExpanded) =>
                  handleExpandClick(it, newExpanded)
                }
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Text>{startCase(it)}</Text>
                </AccordionSummary>
                <AccordionDetails sx={{ padding: 1 }}>
                  <Flex flexDirection={"column"} gap={1}>
                    <Input
                      textFieldProps={{
                        type: "text",
                        name: it,
                        value:
                          localFilters[it as keyof PatientPaymentFilters] ?? "",
                        onChange: (e) =>
                          setLocalFilters({
                            ...localFilters,
                            [it as keyof PatientPaymentFilters]: e.target.value,
                          }),
                        placeholder: `Enter ${startCase(it)}`,
                      }}
                    />
                  </Flex>
                </AccordionDetails>
              </Accordion>
            );
          })}

          <Box minWidth={220}>
            <Flex width={"100%"} justifyContent={"end"}>
              <Button
                variant="text"
                sx={{ color: "gray" }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                variant="text"
                onClick={handleApply}
                disabled={hasInvalidDate}
              >
                Apply
              </Button>
            </Flex>
          </Box>
        </GlobalFilter>
        <UpsertPatientPayments
          title={`${
            activePatientPayment === "patient-payment"
              ? "Collect Patient Payment"
              : "Add Insurance Payment"
          }`}
          disclosureHost={patientPaymentDisclosureHost}
        />
        {/* Add Insurance Payment removed. The button used to be shared across
        both tabs, rendering as "Add Insurance Payment" on the insurance tab:

        {(hasUpdatePayments || hasUpdateInsurancePayments) && (
          <StyledButtonV2
            label={`${
              activePatientPayment === "patient-payment"
                ? "Collect Patient Payment"
                : "Add Insurance Payment"
            }`}
            variant="contained"
            onClick={() => patientPaymentDisclosureHost.open()}
          />
        )}

        It is now limited to the Patient Payment tab. */}
        {activePatientPayment === "patient-payment" &&
          (hasUpdatePayments || hasUpdateInsurancePayments) && (
            <StyledButtonV2
              label="Collect Patient Payment"
              variant="contained"
              onClick={() => patientPaymentDisclosureHost.open()}
            />
          )}
      </Flex>
    );
  }
);
